"use client";

import React from "react";
import { Stethoscope, Award, Activity, ShieldCheck, CheckCircle2, UserCheck } from "lucide-react";
import { DENTAL_DOCTORS, DENTAL_TECHNOLOGIES } from "@/lib/demos/dentalData";

interface ClinicalFacultySectionProps {
  onOpenBooking: () => void;
}

export default function ClinicalFacultySection({
  onOpenBooking,
}: ClinicalFacultySectionProps) {
  return (
    <section className="py-16 sm:py-24 bg-slate-950 text-white border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        {/* Doctors Header */}
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/30 text-[11px] font-bold text-teal-400 uppercase tracking-wider">
            <UserCheck className="w-3.5 h-3.5" />
            <span>Surgical &amp; Cosmetic Faculty</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-white">
            Internationally Certified Dental Specialists
          </h2>

          <p className="text-xs sm:text-sm text-slate-400">
            Every procedure is planned and executed by specialized MDS consultants and international fellows.
          </p>
        </div>

        {/* 4 Doctor Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DENTAL_DOCTORS.map((doc) => (
            <div
              key={doc.id}
              className="p-6 rounded-3xl bg-slate-900/60 border border-slate-800/80 space-y-4 hover:border-teal-500/40 transition-colors flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-white">{doc.name}</h4>
                  <p className="text-[11px] text-teal-400 font-semibold">{doc.title}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{doc.degrees}</p>
                </div>

                <div className="pt-2 border-t border-slate-800/80 space-y-1.5 text-xs text-slate-300">
                  <p><strong className="text-slate-400">Specialty:</strong> {doc.specialty}</p>
                  <p className="text-[10px] text-slate-400 italic pt-1">{doc.fellowships}</p>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px]">
                <span className="text-slate-400 font-medium">{doc.experience}</span>
                <span className="text-teal-400 font-bold">MDS Specialist</span>
              </div>
            </div>
          ))}
        </div>

        {/* Clinical Technology Showcase */}
        <div className="pt-10 space-y-10 border-t border-slate-800/80">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-[11px] font-bold text-teal-400 uppercase tracking-wider">
              Ambulatory Surgical Infrastructure
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
              100% Digital Hospital-Grade Suite
            </h3>
            <p className="text-xs text-slate-400">
              Eliminating guesswork with sub-millimeter 3D imaging, computer-guided surgical templates, and painless local anesthesia.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {DENTAL_TECHNOLOGIES.map((tech, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-slate-900/40 border border-slate-800/80 space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-teal-500/10 text-teal-400 border border-teal-500/20">
                    {tech.metric}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-white">{tech.name}</h4>
                <p className="text-xs text-slate-400 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center pt-4">
            <button
              onClick={onOpenBooking}
              className="py-3 px-8 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-black text-xs uppercase tracking-wide transition-all shadow-lg shadow-teal-500/20 hover:scale-[1.02]"
            >
              Book Specialist 3D Scan &amp; Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
